import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Play, Pause, Loader2, Zap, FileText, ListChecks } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * BurnEnrichmentCard (iter158 · rev iter41-testing)
 *
 * Fires bounded Claude batches on demand. Backend runs them as background
 * tasks (fire-and-forget) so Cloudflare's ~100s edge timeout can't 502 the
 * admin. The UI polls GET /admin/auto-enrich to observe remaining_*
 * decrease and computes "+N this session" as the delta from first-open.
 */
export default function BurnEnrichmentCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const stopRef = useRef(false);
  const baselineRef = useRef({ desc: null, fac: null });

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/auto-enrich");
      setStatus(data);
      if (baselineRef.current.desc === null) {
        baselineRef.current = {
          desc: data.remaining_descriptions,
          fac: data.remaining_facilities,
        };
      }
      return data;
    } catch { return null; }
  };

  useEffect(() => { loadStatus(); }, []);

  // Poll for background burn task completion. Resolves when the backend
  // task has finished (or after `timeoutMs`).
  const waitForBurn = async (timeoutMs = 120000) => {
    const start = Date.now();
    // Give the backend a tick to actually start the task.
    await new Promise((r) => setTimeout(r, 800));
    while (Date.now() - start < timeoutMs) {
      try {
        const { data } = await api.get("/admin/auto-enrich/burn-batch/status");
        if (!data.running) {
          await loadStatus();
          if (data.last_result?.error) {
            toast.error(`Batch error: ${data.last_result.error.slice(0, 120)}`);
          }
          return data.last_result || { enriched: 0 };
        }
      } catch { /* keep polling */ }
      // Refresh remaining counters mid-flight so the UI feels live.
      await loadStatus();
      await new Promise((r) => setTimeout(r, 3000));
    }
    // Timed out — refresh status one final time.
    await loadStatus();
    return { enriched: 0, timed_out: true };
  };

  const burnOne = async (opts = {}) => {
    const isTransient = (e) => {
      const s = e?.response?.status;
      return s === 502 || s === 503 || s === 504;
    };
    setBusy(true);
    try {
      // Retry POST once on transient 5xx (edge origin-warmup).
      let started = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const { data } = await api.post("/admin/auto-enrich/burn-batch",
            { batch_size: opts.batchSize ?? 10, concurrency: 2 });
          started = data;
          break;
        } catch (e) {
          if (attempt === 0 && isTransient(e)) {
            await new Promise((r) => setTimeout(r, 2500));
            continue;
          }
          throw e;
        }
      }
      if (!started) return { done: true };
      if (started.status === "done" || started.phase === "done") {
        toast.success("Catalogue fully enriched — nothing left to do!");
        setAuto(false);
        return { done: true };
      }
      if (started.status === "already_running") {
        toast.message("A burn task is already running — waiting for it.");
      }
      const result = await waitForBurn();
      if (result.timed_out) {
        toast.message("Batch still running in the background — counters will update as it completes.");
      }
      return { done: false };
    } catch (e) {
      toast.error(formatErr(e));
      return { done: true };
    } finally { setBusy(false); }
  };

  const startAuto = async () => {
    stopRef.current = false;
    setAuto(true);
    while (!stopRef.current) {
      const res = await burnOne();
      if (res.done) break;
      // Short pause between batches.
      await new Promise((r) => setTimeout(r, 1200));
    }
    setAuto(false);
  };

  const stopAuto = () => {
    stopRef.current = true;
    setAuto(false);
  };

  const remainingDesc = status?.remaining_descriptions ?? "—";
  const remainingFac = status?.remaining_facilities ?? "—";
  const sessDesc = baselineRef.current.desc != null && typeof remainingDesc === "number"
    ? Math.max(0, baselineRef.current.desc - remainingDesc) : 0;
  const sessFac = baselineRef.current.fac != null && typeof remainingFac === "number"
    ? Math.max(0, baselineRef.current.fac - remainingFac) : 0;

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-burn-enrichment"
    >
      <div className="min-w-0 mb-4">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
          <Zap className="w-3 h-3" /> AI enrichment
        </div>
        <h2 className="font-display text-xl sm:text-2xl tracking-tight">
          Burn the enrichment backlog
        </h2>
        <p className="text-xs text-[#666] mt-1 max-w-xl">
          Fires bounded 10-doc Claude batches as background tasks and polls
          for progress — so slow batches never 502 at the edge. Auto-mode
          loops until the catalogue is fully enriched.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="border hairline rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#666] flex items-center gap-1">
            <FileText className="w-3 h-3" /> Descriptions remaining
          </div>
          <div className="font-display text-2xl mt-1" data-testid="burn-enrichment-desc-remaining">{remainingDesc}</div>
          <div className="text-[10px] text-[#0a7f3f] mt-0.5">
            +{sessDesc} this session
          </div>
        </div>
        <div className="border hairline rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#666] flex items-center gap-1">
            <ListChecks className="w-3 h-3" /> Facilities remaining
          </div>
          <div className="font-display text-2xl mt-1" data-testid="burn-enrichment-fac-remaining">{remainingFac}</div>
          <div className="text-[10px] text-[#0a7f3f] mt-0.5">
            +{sessFac} this session
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {!auto ? (
          <button
            type="button"
            onClick={startAuto}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60"
            data-testid="burn-enrichment-auto-btn"
          >
            <Play className="w-4 h-4" /> Auto-burn until done
          </button>
        ) : (
          <button
            type="button"
            onClick={stopAuto}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hairline text-sm font-black uppercase tracking-wider bg-white hover:bg-[#f6f6f6]"
            data-testid="burn-enrichment-stop-btn"
          >
            <Pause className="w-4 h-4" /> Stop auto-burn
          </button>
        )}
        <button
          type="button"
          onClick={() => burnOne()}
          disabled={busy || auto}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hairline text-sm font-semibold hover:bg-[#fff4ee] disabled:opacity-60"
          data-testid="burn-enrichment-one-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Burn 1 batch (10 docs)
        </button>
      </div>
    </section>
  );
}
